import { Map } from 'immutable';
import { createSelector } from 'reselect';

export const getGuestDirectoryState = state => state.get('guestDirectory', Map({ menus: Map() }));

export const getGuestDirectoryMenusMap = createSelector([getGuestDirectoryState], guestDirectoryState =>
  guestDirectoryState.get('menus', new Map())
);

export const getGuestDirectoryMenus = createSelector([getGuestDirectoryMenusMap], menusMap =>
  menusMap
    .valueSeq()
    .filter(menu => menu.get('enabled') && !menu.get('deleted_at'))
    .sortBy(menu => menu.get('sort_order') || 0)
    .map(menu => menu.toJS())
    .toArray()
);

export const getGuestDirectoryIsLoading = createSelector([getGuestDirectoryState], guestDirectoryState =>
  guestDirectoryState.get('isLoading', false)
);

export const getGuestDirectoryError = createSelector([getGuestDirectoryState], guestDirectoryState =>
  guestDirectoryState.get('error')
);

export const getGuestDirectoryMenuBySlug = (state, menuSlug) => {
  if (!menuSlug) return null;
  const menusMap = getGuestDirectoryMenusMap(state);
  const menu = menusMap.get(menuSlug);
  return menu ? menu.toJS() : null;
};

export const getGuestDirectorySectionsByMenuSlug = (state, menuSlug) => {
  const menusMap = getGuestDirectoryMenusMap(state);
  const menu = menusMap.get(menuSlug);
  if (!menu) return [];

  const sectionsMap = menu.get('sections', new Map());
  return sectionsMap
    .valueSeq()
    .filter(section => section.get('enabled') && !section.get('deleted_at'))
    .sortBy(section => section.get('sort_order') || 0)
    .map(section => section.toJS())
    .toArray();
};

export const getGuestDirectorySectionBySlug = (state, menuSlug, sectionSlug) => {
  const menusMap = getGuestDirectoryMenusMap(state);
  const menu = menusMap.get(menuSlug);
  if (!menu) return null;

  const section = menu.getIn(['sections', sectionSlug]);
  return section ? section.toJS() : null;
};

export const getGuestDirectoryContentsBySectionSlug = (state, menuSlug, sectionSlug) => {
  const menusMap = getGuestDirectoryMenusMap(state);
  const menu = menusMap.get(menuSlug);
  if (!menu) return [];

  const section = menu.getIn(['sections', sectionSlug]);
  if (!section) return [];

  const contentsMap = section.get('contents', new Map());
  return contentsMap
    .valueSeq()
    .filter(content => content.get('enabled') && !content.get('deleted_at'))
    .sortBy(content => content.get('sort_order') || 0)
    .map(content => content.toJS())
    .toArray();
};

export const getAllGuestDirectorySections = createSelector([getGuestDirectoryMenusMap], menusMap =>
  menusMap.reduce((allSections, menu) => {
    const sectionsMap = menu.get('sections', new Map());
    const enabledSections = sectionsMap
      .valueSeq()
      .filter(section => section.get('enabled') && !section.get('deleted_at'))
      .map(section => section.toJS())
      .toArray();
    return allSections.concat(enabledSections);
  }, [])
);

export const getAllGuestDirectoryContents = createSelector([getGuestDirectoryMenusMap], menusMap =>
  menusMap.reduce((allContents, menu) => {
    const sectionsMap = menu.get('sections', new Map());
    const menuContents = sectionsMap.reduce((sectionContents, section) => {
      const contentsMap = section.get('contents', new Map());
      const enabledContents = contentsMap
        .valueSeq()
        .filter(content => content.get('enabled') && !content.get('deleted_at'))
        .map(content => content.toJS())
        .toArray();
      return sectionContents.concat(enabledContents);
    }, []);
    return allContents.concat(menuContents);
  }, [])
);

export const getGuestDirectorySearchData = createSelector([getGuestDirectoryMenusMap], menusMap => {
  const searchData = [];

  menusMap.forEach((menu, menuSlug) => {
    if (!menu.get('enabled') || menu.get('deleted_at')) return;

    const menuNames = [];
    const menuTranslations = menu.get('translations');
    const menuName = menu.get('name');
    if (menuName) menuNames.push(menuName);
    if (menuTranslations) {
      menuTranslations.forEach(translation => {
        const translationName = translation && translation.get ? translation.get('name') : translation?.name;
        if (translationName) menuNames.push(translationName);
      });
    }

    searchData.push({
      type: 'menu',
      id: menu.get('id'),
      menuSlug,
      name: menuNames.join(' '),
      url: `/guest-directory/menu/${menuSlug}`,
    });

    const sectionsMap = menu.get('sections', new Map());
    sectionsMap.forEach((section, sectionSlug) => {
      if (!section.get('enabled') || section.get('deleted_at')) return;

      const sectionNames = [];
      const sectionTranslations = section.get('translations');
      const sectionName = section.get('name');
      if (sectionName) sectionNames.push(sectionName);
      if (sectionTranslations) {
        sectionTranslations.forEach(translation => {
          const translationName =
            translation && translation.get ? translation.get('name') : translation?.name;
          if (translationName) sectionNames.push(translationName);
        });
      }

      searchData.push({
        type: 'section',
        id: section.get('id'),
        menuSlug,
        sectionSlug,
        name: sectionNames.join(' '),
        url: `/guest-directory/menu/${menuSlug}/section/${sectionSlug}`,
      });

      const contentsMap = section.get('contents', new Map());
      contentsMap.forEach((content, contentId) => {
        if (!content.get('enabled') || content.get('deleted_at')) return;

        const contentTitles = [];
        const contentBodies = [];
        const contentTranslations = content.get('translations');

        const contentName = content.get('name');
        const contentBody = content.get('content');
        if (contentName) contentTitles.push(contentName);
        if (contentBody) contentBodies.push(contentBody);

        if (contentTranslations) {
          contentTranslations.forEach(translation => {
            const translationName =
              translation && translation.get ? translation.get('name') : translation?.name;
            const translationContent =
              translation && translation.get ? translation.get('content') : translation?.content;
            if (translationName) contentTitles.push(translationName);
            if (translationContent) contentBodies.push(translationContent);
          });
        }

        searchData.push({
          type: 'content',
          id: contentId,
          menuSlug,
          sectionSlug,
          contentId,
          name: contentTitles.join(' '),
          content: contentBodies.join(' '),
          url: `/guest-directory/menu/${menuSlug}/section/${sectionSlug}`,
        });
      });
    });
  });

  return searchData;
});

export const getGuestDirectoryRowColour = state =>
  state.getIn(['branding', 'guestDirectory', 'rowColour']) || null;

export const getGuestDirectoryAlternateRowColour = state =>
  state.getIn(['branding', 'guestDirectory', 'alternateRowColour']) || null;

export const getGuestDirectoryServiceIconPath = state =>
  state.getIn(['branding', 'guestDirectory', 'serviceIconPath']) || null;

export const getGuestDirectoryLinkButtonColour = state =>
  state.getIn(['branding', 'guestDirectory', 'linkButtonColour']) || null;

export const getGuestDirectoryLinkButtonBg = state =>
  state.getIn(['branding', 'guestDirectory', 'linkButtonBg']) || null;

export const getGuestDirectoryLinkButtonBorder = state =>
  state.getIn(['branding', 'guestDirectory', 'linkButtonBorder']) || null;
